import React, { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Download, HelpCircle, Phone } from "lucide-react";

const NAVY = "#0F2C59";

export const GUIDE_STEPS = [
  {
    no: 1, img: "adim01.png", title: "Telefonla sisteme girin",
    text: "Kullanıcı adınızı ve şifrenizi yazın. Şifreyi görmek için gözlük işaretine dokunabilirsiniz.",
  },
  {
    no: 2, img: "adim02.png", title: "“Giriş” düğmesine basın",
    text: "Bilgileri yazdıktan sonra mavi “Giriş” düğmesine bir kez dokunun.",
  },
  {
    no: 3, img: "adim03.png", title: "Seçim Çetelesi → Komite Sandık",
    text: "Alt menüdeki “Menü”den Seçim Çetelesi’ni açın. “Komite Sandık” yazan düğmenin seçili olduğundan emin olun. Aşağıdaki listeden kendi komitenize dokunun.",
  },
  {
    no: 4, img: "adim04.png", title: "“Bekleyen” sekmesi: henüz gelmeyenler",
    text: "Komiteniz açıldığında “Bekleyen” sekmesinde henüz oy kullanmamış firmalar görünür. Sayı, kaç firmanın beklediğini gösterir.",
  },
  {
    no: 5, img: "adim05.png", title: "Gelen seçmeni arayın",
    text: "Arama kutusuna firma adının birkaç harfini ya da yetkilinin adını yazın. Liste hemen kısalır.",
  },
  {
    no: 6, img: "adim06.png", title: "Firmaya dokunun, oyu işaretleyin",
    text: "Üç büyük düğme çıkar: YEŞİL = KESİN (bizim oyumuz), GRİ = BELİRSİZ (kime verdiği bilinmiyor), KIRMIZI = KARŞI (rakibe gitti). Hangisi ise ona bir kez dokunun; başka onay gerekmez.",
  },
  {
    no: 7, img: "adim07.png", title: "Hata yaptıysanız hemen “Geri Al”",
    text: "İşaretledikten sonra ekranın altında “Son işaretlenen … Geri Al” çıkar. Yanlış firmaya bastıysanız “Geri Al”a dokunmanız yeterli.",
  },
  {
    no: 8, img: "adim08.png", title: "Sonradan değiştirme veya silme",
    text: "Firmaya tekrar dokunun: mevcut seçim işaretli gelir, başka bir renge dokunarak değiştirebilirsiniz. En alttaki “Oyu geri al (oy kullanmadı)” ile işareti tamamen kaldırırsınız.",
  },
  {
    no: 9, img: "adim09.png", title: "Sayaçların anlamı",
    text: "Üstte “Oy kullanan 12/139 · %9” gibi yazar: komitenizde kaç kişi oy kullandı. Bekleyen / Oy kullanan / Tümü sekmeleri arasında dokunarak gezinebilirsiniz.",
  },
  {
    no: 10, img: "adim10.png", title: "Oy kullananlar üstü çizili görünür",
    text: "“Oy kullanan” sekmesinde firmanın adı üstü çizili ve yeşil tik ile görünür; saat de yazar. Böylece kimin geldiği karışmaz.",
  },
  {
    no: 11, img: "adim11.png", title: "Oy Gücü: çok oyu olan yetkililer",
    text: "“Oy Gücü” sekmesi, 2 ve daha fazla firmanın oyunu kullanacak kişileri en çoktan aza sıralar. Bu kişileri mutlaka arayıp sandığa getirin; biri gelmezse birden fazla oy kaybedilir.",
  },
  {
    no: 12, img: null, title: "İnternet kesilirse ne olur?",
    text: "Telefonunuz çevrimdışı olursa işaretlemeleriniz kaybolmaz; telefonda bekler ve bağlantı gelince kendiliğinden gönderilir. Ekranda “bekleyen işaretleme” uyarısı görürseniz telefonu açık tutmanız yeterlidir.",
  },
  {
    no: 13, img: null, title: "Kâğıt liste size verilecek",
    text: "Her sandık görevlisine seçmen listesinin çıktısı seçim sabahı teslim edilecektir.",
  },
];

export default function BallotGuide() {
  const [busy, setBusy] = useState(false);

  const downloadPdf = async () => {
    setBusy(true);
    try {
      const res = await api.get("/guide/ballot-pdf", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "Secim_Gunu_Cetele_Kilavuzu.pdf";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Kılavuz PDF indirilemedi");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6 pb-10" data-testid="ballot-guide-page">
      <Card className="p-5 md:p-7" style={{ background: `${NAVY}0d` }}>
        <div className="flex flex-wrap items-start gap-4">
          <div className="h-12 w-12 rounded-xl grid place-items-center shrink-0"
            style={{ background: NAVY, color: "white" }}>
            <HelpCircle className="h-6 w-6" />
          </div>
          <div className="min-w-0">
            <h1 className="text-2xl md:text-3xl font-bold" style={{ color: NAVY }}>
              Seçim Günü Çetele Kılavuzu
            </h1>
            <p className="text-base text-muted-foreground mt-1">
              Resimlere bakarak adım adım ilerleyin. Her adımda kırmızı çerçeve, telefonunuzda
              nereye dokunacağınızı gösterir.
            </p>
          </div>
          <Button onClick={downloadPdf} disabled={busy} className="ml-auto"
            style={{ background: NAVY }} data-testid="guide-pdf-btn">
            <Download className="h-4 w-4 mr-2" /> PDF indir / yazdır
          </Button>
        </div>
        <div className="mt-4 rounded-lg bg-card border border-border p-4 text-base">
          <b>Özet:</b> Giriş yapın → <b>Seçim Çetelesi</b> → kendi komiteniz → gelen firmaya dokunun →
          <span className="text-emerald-700 font-semibold"> KESİN</span> /
          <span className="text-slate-600 font-semibold"> BELİRSİZ</span> /
          <span className="text-red-600 font-semibold"> KARŞI</span> seçin. Hepsi bu kadar.
        </div>
      </Card>

      <div className="space-y-6">
        {GUIDE_STEPS.map((s) => (
          <Card key={s.no} className="p-4 md:p-6" data-testid={`guide-step-${s.no}`}>
            <div className="flex flex-col md:flex-row gap-5">
              <div className="flex items-start gap-3 md:w-[46%] shrink-0">
                <span className="h-10 w-10 rounded-full grid place-items-center text-lg font-bold text-white shrink-0"
                  style={{ background: "#DC2626" }}>{s.no}</span>
                <div>
                  <h2 className="text-lg md:text-xl font-bold leading-snug">{s.title}</h2>
                  <p className="text-base mt-2 leading-relaxed">{s.text}</p>
                </div>
              </div>
              {s.img && (
                <div className="md:flex-1 flex justify-center">
                  <img src={`/guide/${s.img}`} alt={s.title}
                    className="rounded-xl border-2 border-border shadow-sm max-h-[520px] w-auto"
                    data-testid={`guide-img-${s.no}`} />
                </div>
              )}
            </div>
          </Card>
        ))}
      </div>

      <Card className="p-5 text-base" data-testid="guide-help">
        <div className="flex items-center gap-3">
          <Phone className="h-5 w-5" style={{ color: NAVY }} />
          <span>
            Takılırsanız merkezi arayın: ekranınızı kapatmayın, hangi adımda olduğunuzu söylemeniz
            yeterli. Yaptığınız her işaretleme merkezden anında görülür.
          </span>
        </div>
      </Card>
    </div>
  );
}
